// IMP-448 — 알림 메일 티켓 링크(예: /Service/TK-1)를 로그인 계정 권한에 맞는 화면
// (어드민 /Admin/AdminService/:ticketKey 또는 일반 /Service/:ticketKey)으로 판정하는 SSoT.
//
// 왜 evaluateRouteAccess 를 재사용하는가: "이 계정이 어드민 티켓 화면에 들어갈 수 있는가"는
// AuthRoute/AdminAuthRoute 가 evaluateRouteAccess(rbacGuard.js)로 이미 판정하는 질문과 동일하다.
// 여기서 role → 어드민 여부를 별도로 다시 계산하면 두 판정이 갈라질 위험이 있으므로
// (CLAUDE.md §6/§8 — 계산은 한 개념당 하나의 공개 함수), getLicenseRole(licenseHelper.js)로
// role 을 구해 evaluateRouteAccess 에 어드민 티켓 경로를 넣고 그 결과(allow)만 따른다.
// 신규 truth table 은 만들지 않는다.

import { getLicenseRole } from './licenseHelper';
import { evaluateRouteAccess, normalizePathname } from './rbacGuard';
import { ROUTES } from '../config/routes';

// 라우트 문자열은 ROUTES SSoT 에서 가져온다(routes.test.js 가 app.js 선언과의 일치를 강제).
// 두 번째 항목(물음표 없는 변형)은 과거 AuthRoute.js 의 리터럴 비교와의 호환을 위해 유지.
export const TICKET_DEEP_LINK_ROUTE_PATHS = [
    ROUTES.SERVICE_TICKET.replace(/\?$/, ''),
    ROUTES.SERVICE_TICKET,
];

// ROUTES 값은 param 을 포함한 라우트 선언이므로, 경로 조립에 쓸 접두사만 떼어낸다.
const stripRouteParams = (routePath) => routePath.replace(/\/:.*$/, '');
const SERVICE_BASE = stripRouteParams(ROUTES.SERVICE_TICKET);
const ADMIN_SERVICE_BASE = stripRouteParams(ROUTES.ADMIN_SERVICE_TICKET);

export const isTicketDeepLinkPath = (routePath) =>
    typeof routePath === 'string' && TICKET_DEEP_LINK_ROUTE_PATHS.includes(routePath);

// 진입 컨텍스트 — 모듈 로드 시점(=문서 로드 시점)에 1회 고정한다. SPA 내비게이션
// (history.push)으로는 바뀌지 않으므로 "이 문서를 어떻게 열었는가"의 판정 기준이 된다.
// app.js:133 의 isAdmin 이 같은 이유로 마운트 시점 pathname 을 고정하는 것과 동일한 전제.
//
// pathname 만으로는 "메일에서 주소를 열고 들어온 진입"과 "앱 안에서 이미 보던 화면을 F5
// 새로고침 / Ctrl+클릭으로 새 탭에 연 것"을 구분할 수 없다(L1 F-001). 앞의 둘은 승격 대상이
// 아니므로 두 신호를 더 본다.
//   - navigation type 이 'navigate' 가 **아니면** 전부 제외(fail-closed). 'reload'(F5),
//     'back_forward'(뒤로가기 복원), 'prerender', 그리고 API 미지원·throw·빈 배열로 신호를
//     못 읽은 경우가 모두 여기 들어간다. 신호를 모를 때 승격하는 쪽으로 기울면 L2 F-004 처럼
//     오승격이 되살아나므로, 모르면 이동하지 않는다.
//   - document.referrer 가 같은 오리진 → 앱 내 링크에서 열린 문서. 승격하지 않는다.
// 남는 것(referrer 없음 = 메일 클라이언트, referrer 가 외부 오리진 = 웹메일)이 딥링크 진입이다.
//
// 미로그인 상태로 메일 링크를 연 경우는 이 추론에 기대지 않는다 — AuthRoute 가 저장한 mailLink 를
// 로그인 시점에 resolveMailLinkTarget 으로 해석해 목적지 자체를 정한다(L2 F-003). 여기 추론이
// 필요한 경우는 "이미 로그인된 상태에서 메일 링크를 연" 진입 하나뿐이다.
// 한계: 북마크·주소 직접 입력도 같은 신호를 내므로 메일 링크와 구분되지 않는다. 메일 템플릿에
// 마커를 심을 수 없어(고객 대면·기발송분 존재) 외부 진입으로 동일 취급한다.
const readEntryContext = () => {
    if (typeof window === 'undefined') return { pathname: '', deepLink: false };

    const pathname = window.location?.pathname ?? '';

    let navType = '';
    try {
        navType = window.performance?.getEntriesByType?.('navigation')?.[0]?.type ?? '';
    } catch {
        // performance API 미지원/차단 — 신호 없음으로 취급(referrer 판정만 사용)
        navType = '';
    }

    let referrer = '';
    try {
        referrer = (typeof document !== 'undefined' ? document.referrer : '') ?? '';
    } catch {
        referrer = '';
    }

    const origin = window.location?.origin ?? '';
    const sameOriginReferrer = !!referrer && !!origin && referrer.startsWith(origin);

    return { pathname, deepLink: navType === 'navigate' && !sameOriginReferrer };
};

const ENTRY = readEntryContext();

export const getEntryPathname = () => ENTRY.pathname;

// 앱 바깥에서 주소를 열고 들어온 진입인가(= 메일 링크 클릭 등).
export const isDeepLinkEntry = () => ENTRY.deepLink;

// user/ticketKey 는 미로그인 잔여 호출 등으로 undefined 가능 — null-safe.
export const resolveTicketRoute = (user, ticketKey) => {
    if (typeof ticketKey !== 'string' || !ticketKey.trim()) return null;

    const role = getLicenseRole(user);
    const decision = evaluateRouteAccess({
        role,
        permAdminAccess: !!user?.permAdminAccess,
        pathname: `${ADMIN_SERVICE_BASE}/${ticketKey}`,
    });

    const encoded = encodeURIComponent(ticketKey);
    return decision?.allow === true
        ? `${ADMIN_SERVICE_BASE}/${encoded}`
        : `${SERVICE_BASE}/${encoded}`;
};

// 이미 목적지에 있으면 null(리다이렉트 불필요) — 무한 리다이렉트/불필요한 hard-reload 방지.
//
// 본 게이트는 "일반 티켓 화면 → 어드민 티켓 화면" 승격 한 방향만, 그리고 **딥링크 하드 진입에
// 한해서만** 수행한다.
//   - 승격 한 방향: 호출부가 일반 티켓 화면(ServiceDetail) 자체라 일반 목적지는 정의상 현재
//     화면이다. pathname 문자열 비교만으로 판정하면 실제 경로를 읽지 못하는 환경(pathname='/')
//     에서 자기 화면으로 헛 이동해 렌더·fetch 가 통째로 중단된다.
//   - 딥링크 한정(R1 M-1): 테크 포털 목록은 권한과 무관하게 항상 `/Service/:ticketKey` 로
//     링크한다(ServiceCard.js:137). 진입 전체에 승격을 걸면 어드민 계정이 테크 포털 티켓
//     화면에 아예 머물 수 없다(CLAUDE.md §8 "선택 가능한데 항상 다른 곳으로 가는 항목").
//     IMP-448 의 대상은 메일 링크로 주소를 직접 열고 들어온 경우뿐이므로, 진입 경로
//     (getEntryPathname)가 현재 경로와 같을 때만 승격한다.
// entryPathname / isDeepLinkEntry 미전달은 fail-closed(이동 안 함)로 처리한다.
export const resolveTicketRedirect = ({
    user,
    ticketKey,
    currentPathname,
    entryPathname,
    isDeepLinkEntry: deepLinkEntry,
} = {}) => {
    const dest = resolveTicketRoute(user, ticketKey);
    if (!dest) return null;
    const normalizedDest = normalizePathname(dest);
    if (!normalizedDest.startsWith(normalizePathname(`${ADMIN_SERVICE_BASE}/`))) return null;
    const normalizedCurrent = normalizePathname(currentPathname);
    if (normalizedDest === normalizedCurrent) return null;
    if (!entryPathname) return null;
    if (normalizePathname(entryPathname) !== normalizedCurrent) return null;
    // 새로고침·앱 내 새 탭은 딥링크가 아니다(L1 F-001). 미전달도 fail-closed.
    if (deepLinkEntry !== true) return null;
    return dest;
};

// 미로그인 진입 시 AuthRoute 가 저장해 둔 mailLink 를 로그인 시점에 해석한다.
// 티켓 딥링크면 계정 권한에 맞는 목적지로 바꾸고, 그 외 경로는 손대지 않는다.
// 이 경로가 있어야 "메일에서 왔는가"를 브라우저 신호로 추론하지 않아도 된다(L2 F-003).
const MAIL_LINK_TICKET_RE = /^\/service\/([^/?#]+)$/i;

export const resolveMailLinkTarget = (user, mailLink) => {
    if (typeof mailLink !== 'string' || !mailLink) return mailLink;
    const matched = MAIL_LINK_TICKET_RE.exec(mailLink);
    if (!matched) return mailLink;
    let ticketKey = matched[1];
    try {
        ticketKey = decodeURIComponent(ticketKey);
    } catch {
        // malformed URI 는 원본 세그먼트 그대로 사용
    }
    return resolveTicketRoute(user, ticketKey) ?? mailLink;
};
